import React, { useEffect, useState, useRef } from 'react';
import { Linkedin, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { consumeOAuthState } from '../lib/oauthState';

interface LinkedInProfile {
  id: string;
  name: string;
  type: 'person' | 'organization';
}

interface LinkedInCallbackProps {
  onComplete: () => void;
}

export function LinkedInCallback({ onComplete }: LinkedInCallbackProps) {
  const [status, setStatus] = useState<'processing' | 'selecting' | 'saving' | 'success' | 'error'>('processing');
  const [error, setError] = useState<string>('');
  const [profiles, setProfiles] = useState<LinkedInProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  // Token LinkedIna zostaje na serwerze - przegladarka zna tylko identyfikator schowka.
  const [pendingId, setPendingId] = useState<string>('');

  // CRITICAL FIX: Prevent double execution in React Strict Mode
  const calledRef = useRef(false);

  useEffect(() => {
    // Guard against double execution
    if (calledRef.current) return;
    calledRef.current = true;

    handleLinkedInCallback();
  }, []);

  const handleLinkedInCallback = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const error = params.get('error');
      const errorDescription = params.get('error_description');

      // Zanim cokolwiek zrobimy z kodem: state musi pasowac do autoryzacji zaczetej w tej przegladarce.
      consumeOAuthState('linkedin', params.get('state'));

      if (error) {
        throw new Error(`LinkedIn authorization failed: ${errorDescription || error}`);
      }

      if (!code) {
        throw new Error('No authorization code received from LinkedIn');
      }

      window.history.replaceState({}, document.title, window.location.pathname);

      setStatus('processing');

      // Funkcja brzegowa bierze uzytkownika z tokena sesji, nie z ciala zadania.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Musisz byc zalogowany, zeby podlaczyc konto LinkedIn');
      }

      const redirectUri = `${window.location.origin}/linkedin-callback`;
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(`${supabaseUrl}/functions/v1/linkedin-oauth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'exchange',
          code,
          redirectUri,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Show user-friendly message for invalid_client errors (custom credentials issue)
        if (errorData.hint) {
          throw new Error(errorData.hint);
        }
        
        // Parse LinkedIn error details for better messaging
        let userMessage = errorData.error || 'Failed to exchange authorization code';
        if (errorData.details) {
          try {
            const details = typeof errorData.details === 'string' ? JSON.parse(errorData.details) : errorData.details;
            if (details.error === 'invalid_client') {
              userMessage = 'Twoje własne credentials LinkedIn są nieprawidłowe. Zaktualizuj Client ID i Client Secret w Settings → API Settings lub usuń je, aby korzystać z domyślnych.';
            } else if (details.error_description) {
              userMessage = `${errorData.error}: ${details.error_description}`;
            }
          } catch {
            userMessage = `${errorData.error} (${errorData.details})`;
          }
        }
        
        throw new Error(userMessage);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to authenticate with LinkedIn');
      }

      setPendingId(data.pendingId);

      const profileList: LinkedInProfile[] = (data.profiles || []).map(
        (profile: LinkedInProfile) => ({
          id: profile.id,
          name: profile.name,
          type: profile.type,
        })
      );

      if (profileList.length === 0) {
        throw new Error('LinkedIn nie zwrocil zadnego profilu do podlaczenia');
      }

      setProfiles(profileList);
      setStatus('selecting');

      if (profileList.length === 1) {
        setSelectedProfileId(profileList[0].id);
        await saveSelectedProfile(profileList[0], data.pendingId);
      }
    } catch (err) {
      console.error('LinkedIn callback error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setStatus('error');
      toast.error(err instanceof Error ? err.message : 'Failed to connect LinkedIn');
    }
  };

  const saveSelectedProfile = async (profile: LinkedInProfile, pending: string) => {
    try {
      setStatus('saving');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      // Zapis robi funkcja brzegowa - to ona ma token LinkedIna.
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/linkedin-oauth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'select',
          pendingId: pending,
          profileId: profile.id,
        }),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
        if (result?.error === 'pending_expired') {
          throw new Error('Minelo zbyt duzo czasu na wybor profilu. Podlacz konto LinkedIn jeszcze raz.');
        }
        throw new Error(result?.details || result?.error || 'Failed to save LinkedIn profile');
      }

      setStatus('success');
      toast.success(`LinkedIn ${profile.type === 'person' ? 'profile' : 'page'} connected successfully!`);

      setTimeout(() => {
        const redirectUrl = localStorage.getItem('linkedin_auth_redirect') || '/';
        localStorage.removeItem('linkedin_auth_redirect');
        window.location.href = redirectUrl;
      }, 2000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to save LinkedIn profile');
      setStatus('error');
      toast.error(err instanceof Error ? err.message : 'Failed to save profile');
    }
  };

  const handleProfileSelection = async () => {
    if (!selectedProfileId) {
      toast.error('Please select a profile or page');
      return;
    }

    const selectedProfile = profiles.find(p => p.id === selectedProfileId);
    if (!selectedProfile) {
      toast.error('Selected profile not found');
      return;
    }

    await saveSelectedProfile(selectedProfile, pendingId);
  };

  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <RefreshCw className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Connecting LinkedIn</h2>
          <p className="text-gray-600">Please wait while we complete the connection...</p>
        </div>
      </div>
    );
  }

  if (status === 'selecting') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="flex items-center justify-center mb-6">
            <Linkedin className="w-12 h-12 text-blue-700" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            Select Profile or Page
          </h2>
          <p className="text-gray-600 mb-6 text-center">
            Choose which LinkedIn profile or company page you want to connect
          </p>

          <div className="space-y-3 mb-6">
            {profiles.map((profile) => (
              <label
                key={profile.id}
                className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  selectedProfileId === profile.id
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <input
                  type="radio"
                  checked={selectedProfileId === profile.id}
                  onChange={() => setSelectedProfileId(profile.id)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{profile.name}</div>
                  <div className="text-xs text-gray-500">
                    {profile.type === 'person' ? 'Personal Profile' : 'Company Page'}
                  </div>
                </div>
              </label>
            ))}
          </div>

          <button
            onClick={handleProfileSelection}
            disabled={!selectedProfileId}
            className="w-full px-4 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Connect Selected Account
          </button>
        </div>
      </div>
    );
  }

  if (status === 'saving') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <RefreshCw className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Saving Connection</h2>
          <p className="text-gray-600">Completing the setup...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Successfully Connected!</h2>
          <p className="text-gray-600">Your LinkedIn account has been connected. Redirecting...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Connection Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => {
              const redirectUrl = localStorage.getItem('linkedin_auth_redirect') || '/';
              localStorage.removeItem('linkedin_auth_redirect');
              window.location.href = redirectUrl;
            }}
            className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to App
          </button>
        </div>
      </div>
    );
  }

  return null;
}
